import React, { useState, useMemo } from 'react';
import { Search, Calendar, UserCheck, UserX, ChevronDown, ChevronUp } from 'lucide-react';
import Input from '../../../components/ui/Input';

import { formatEasternDateOnly } from '../../../utils/timezone';

const EmployeeAttendanceTable = ({ employees, attendanceRecords, selectedEmployeeId, selectedEmployeeName }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedEmployees, setExpandedEmployees] = useState(new Set());

  // CRITICAL DEBUGGING: Log received props
  console.log('EmployeeAttendanceTable Props:', {
    employeesCount: employees?.length,
    attendanceCount: attendanceRecords?.length,
    selectedEmployeeId,
    firstEmployee: employees?.[0],
    firstAttendance: attendanceRecords?.[0]
  });

  // Group attendance records by employee
  const employeeAttendanceMap = useMemo(() => {
    const map = new Map();
    
    console.log('🔄 Building attendance map...');
    
    attendanceRecords?.forEach(record => {
      const empId = record?.employeeId; // CRITICAL: This should be camelCase after transformation
      if (!empId) {
        console.warn('⚠️ Record missing employeeId:', record);
        return;
      }
      
      if (!map?.has(empId)) {
        map?.set(empId, []);
      }
      map?.get(empId)?.push(record);
    });
    
    console.log('✅ Attendance map built:', {
      totalEmployees: map?.size,
      employeeIds: Array.from(map?.keys())?.slice(0, 5)
    });
    
    return map;
  }, [attendanceRecords]);

  // Filter employees based on search query
  const filteredEmployees = useMemo(() => {
    if (!employees || employees?.length === 0) {
      console.log('⚠️ No employees to filter');
      return [];
    }
    
    let filtered = [...employees];
    
    console.log('🔍 Filtering employees:', {
      totalEmployees: employees?.length,
      selectedEmployeeId,
      searchQuery
    });
    
    // If specific employee is selected from filters, show only that employee
    if (selectedEmployeeId) {
      filtered = filtered?.filter(emp => emp?.id === selectedEmployeeId);
      console.log(`📌 Filtered by selected ID: ${filtered?.length} employee(s)`);
    }
    
    // Apply search filter
    if (searchQuery?.trim()) {
      const query = searchQuery?.toLowerCase()?.trim();
      filtered = filtered?.filter(emp => {
        const name = emp?.userProfiles?.fullName || emp?.userProfiles?.full_name || emp?.name || '';
        const empId = emp?.employeeId || '';
        return name?.toLowerCase()?.includes(query) || empId?.toLowerCase()?.includes(query);
      });
      console.log(`🔎 Filtered by search: ${filtered?.length} employee(s)`);
    }
    
    console.log('✅ Final filtered employees:', filtered?.length);
    return filtered;
  }, [employees, searchQuery, selectedEmployeeId]);

  // Calculate attendance summary for an employee
  const getEmployeeAttendanceSummary = (employeeId) => {
    const records = employeeAttendanceMap?.get(employeeId) || [];
    
    console.log(`📊 Summary for employee ${employeeId}:`, {
      totalRecords: records?.length,
      recordSample: records?.[0]
    });
    
    // CRITICAL DEBUGGING: Log all unique status values for this employee
    if (records?.length > 0) {
      const statusValues = records?.map(r => ({
        attendanceStatus: r?.attendanceStatus,
        attendance_status: r?.attendance_status,
        raw: JSON.stringify(r)?.substring(0, 100)
      }));
      console.log(`🔍 All status values for employee ${employeeId}:`, statusValues);
    }
    
    const summary = {
      total: records?.length || 0,
      present: 0,
      absent: 0,
      late: 0,
      halfDay: 0,
      onLeave: 0
    };
    
    records?.forEach(record => {
      // CRITICAL DEBUG: Log every record's status before checking
      const statusCamel = record?.attendanceStatus;
      const statusSnake = record?.attendance_status;
      
      console.log(`🔍 Checking record status:`, {
        employeeId,
        date: record?.attendanceDate,
        statusCamel,
        statusSnake,
        isHalfDaySnake: statusSnake === 'halfday',
        isHalfDayCamel: statusCamel === 'halfday'
      });
      
      // UPDATED: Now checking for 'halfday' (single word) instead of 'half_day' or 'halfDay'
      const status = statusCamel || statusSnake;
      
      if (status === 'present') {
        summary.present++;
      } else if (status === 'absent') {
        summary.absent++;
      } else if (status === 'late') {
        summary.late++;
      } else if (status === 'halfday') {
        // UPDATED: Now only checking for 'halfday' (single word)
        console.log('✅ FOUND HALF DAY RECORD:', record);
        summary.halfDay++;
      } else if (status === 'on_leave' || status === 'onLeave') {
        summary.onLeave++;
      } else {
        console.warn(`⚠️ Unknown status detected: "${status}" for record:`, record);
      }
    });
    
    console.log(`✅ Final summary for employee ${employeeId}:`, summary);
    
    return summary;
  };

  // Toggle employee row expansion
  const toggleEmployeeExpansion = (employeeId) => {
    setExpandedEmployees(prev => {
      const newSet = new Set(prev);
      if (newSet?.has(employeeId)) {
        newSet?.delete(employeeId);
      } else {
        newSet?.add(employeeId);
      }
      return newSet;
    });
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return formatEasternDateOnly(dateString, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get status badge color
  const getStatusBadgeClass = (status) => {
    // UPDATED: Normalize status - 'halfday' is now single word
    const normalizedStatus = status?.toLowerCase();
    
    const statusMap = {
      'present': 'bg-green-100 text-green-800 border-green-300',
      'absent': 'bg-red-100 text-red-800 border-red-300',
      'late': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      'halfday': 'bg-blue-100 text-blue-800 border-blue-300',  // Updated from half_day/halfDay
      'onleave': 'bg-purple-100 text-purple-800 border-purple-300',
      'on_leave': 'bg-purple-100 text-purple-800 border-purple-300'
    };
    
    return statusMap?.[normalizedStatus] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  // Format status text
  const formatStatus = (status) => {
    // UPDATED: Normalize status - 'halfday' is now single word
    const normalizedStatus = status?.toLowerCase();
    
    const statusMap = {
      'present': 'Present',
      'absent': 'Absent',
      'late': 'Late',
      'halfday': 'Half Day',  // Updated from half_day/halfDay
      'onleave': 'On Leave',
      'on_leave': 'On Leave'
    };
    
    return statusMap?.[normalizedStatus] || status;
  };

  if (!employees || employees?.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-8 text-center">
        <UserX className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">No employees found</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
      {/* Header with Search */}
      <div className="p-4 md:p-6 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h3 className="text-base md:text-lg font-semibold text-gray-800 flex items-center gap-2">
              <UserCheck className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
              Employee Attendance List
            </h3>
            {selectedEmployeeName && (
              <p className="text-xs md:text-sm text-blue-600 mt-1">
                Showing: {selectedEmployeeName}
              </p>
            )}
            {/* DEBUGGING INFO */}
            {process.env.NODE_ENV === 'development' && (
              <p className="text-xs text-gray-500 mt-1">
                Debug: {employees?.length} employees, {attendanceRecords?.length} records, {filteredEmployees?.length} filtered
              </p>
            )}
          </div>
          
          {/* Search Input */}
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search by name or employee ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="pl-10 w-full"
            />
          </div>
        </div>
      </div>
      {/* Employee List */}
      <div className="divide-y divide-gray-200">
        {filteredEmployees?.map((employee) => {
          const employeeId = employee?.id;
          const employeeName = employee?.userProfiles?.fullName || employee?.userProfiles?.full_name || employee?.name || 'Unknown';
          const empCode = employee?.employeeId || 'N/A';
          const isExpanded = expandedEmployees?.has(employeeId);
          const summary = getEmployeeAttendanceSummary(employeeId);
          const attendanceRecordsForEmployee = employeeAttendanceMap?.get(employeeId) || [];

          return (
            <div key={employeeId} className="bg-white">
              {/* Employee Header - Mobile Optimized */}
              <div 
                className="p-4 md:p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => toggleEmployeeExpansion(employeeId)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 md:gap-3">
                      <div className="w-8 h-8 md:w-10 md:h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-xs md:text-sm font-semibold text-blue-600">
                          {employeeName?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm md:text-base font-medium text-gray-800 truncate">{employeeName}</h4>
                        <p className="text-xs md:text-sm text-gray-500">ID: {empCode}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Summary Stats - Responsive Grid */}
                  <div className="hidden sm:flex items-center gap-3 md:gap-6 ml-4">
                    <div className="text-center">
                      <p className="text-xs text-gray-500">Total</p>
                      <p className="text-sm md:text-base font-semibold text-gray-800">{summary?.total}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-500">Present</p>
                      <p className="text-sm md:text-base font-semibold text-green-600">{summary?.present}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-500">Absent</p>
                      <p className="text-sm md:text-base font-semibold text-red-600">{summary?.absent}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-500">Half Day</p>
                      <p className="text-sm md:text-base font-semibold text-blue-600">{summary?.halfDay}</p>
                    </div>
                  </div>
                  
                  {/* Expand/Collapse Icon */}
                  <div className="ml-2 md:ml-4 flex-shrink-0">
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>
                
                {/* Mobile Summary Stats */}
                <div className="sm:hidden mt-3 grid grid-cols-4 gap-2">
                  <div className="text-center">
                    <p className="text-xs text-gray-500">Total</p>
                    <p className="text-sm font-semibold text-gray-800">{summary?.total}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-500">Present</p>
                    <p className="text-sm font-semibold text-green-600">{summary?.present}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-500">Absent</p>
                    <p className="text-sm font-semibold text-red-600">{summary?.absent}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-500">Half</p>
                    <p className="text-sm font-semibold text-blue-600">{summary?.halfDay}</p>
                  </div>
                </div>
              </div>

              {/* Expanded Attendance Records */}
              {isExpanded && (
                <div className="bg-gray-50 border-t border-gray-200">
                  {attendanceRecordsForEmployee?.length === 0 ? (
                    <div className="p-6 md:p-8 text-center">
                      <Calendar className="w-10 h-10 md:w-12 md:h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-sm md:text-base text-gray-600">No attendance records found</p>
                    </div>
                  ) : (
                    <>
                      {/* Mobile Card View */}
                      <div className="md:hidden divide-y divide-gray-200">
                        {attendanceRecordsForEmployee?.map((record, idx) => (
                          <div key={idx} className="p-4 space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-gray-700">
                                {formatDate(record?.attendanceDate)}
                              </span>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusBadgeClass(record?.attendanceStatus || record?.attendance_status)}`}>
                                {formatStatus(record?.attendanceStatus || record?.attendance_status)}
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div>
                                <span className="text-gray-500">Check In:</span>
                                <span className="ml-1 text-gray-700">{record?.checkInTime || '--'}</span>
                              </div>
                              <div>
                                <span className="text-gray-500">Check Out:</span>
                                <span className="ml-1 text-gray-700">{record?.checkOutTime || '--'}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Desktop Table View */}
                      <div className="hidden md:block overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Date
                              </th>
                              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                              </th>
                              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Hours
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {attendanceRecordsForEmployee?.map((record) => (
                              <tr key={record?.id} className="hover:bg-gray-50 transition-colors">
                                <td className="px-6 py-4 whitespace-nowrap">
                                  {formatDate(record?.attendanceDate)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-center">
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusBadgeClass(record?.attendanceStatus)}`}>
                                    {formatStatus(record?.attendanceStatus)}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-center">
                                  {record?.totalHours ? parseFloat(record?.totalHours)?.toFixed(2) : '--'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* Footer Summary */}
      {filteredEmployees?.length > 0 && (
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>
              Showing <span className="font-semibold text-gray-900">{filteredEmployees?.length}</span> employee{filteredEmployees?.length !== 1 ? 's' : ''}
            </span>
            <span>
              Total Records: <span className="font-semibold text-gray-900">{attendanceRecords?.length || 0}</span>
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeAttendanceTable;